import { Skeleton, SectionCard, ui } from './insightUi';

/**
 * Loading placeholders that mirror the real page structure — hero, KPI
 * tiles, chart tiles and a post rail — so the layout doesn't jump when the
 * API responses land.
 */

/** KPI tile placeholders, same 186×60 footprint as the real StatsCard. */
export function StatTilesSkeleton({ count = 6 }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            boxSizing: 'border-box',
            width: 186,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: ui.tileBg,
            border: `1px solid ${ui.borderSoft}`,
            borderRadius: 10,
            padding: '10px 14px',
          }}
        >
          <Skeleton circle height={30} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Skeleton width="70%" height={13} />
            <Skeleton width="50%" height={9} style={{ marginTop: 6 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SectionHeaderSkeleton() {
  return {
    icon: null,
    title: <Skeleton width={150} height={15} />,
    subtitle: <Skeleton width={220} height={11} style={{ marginTop: 6 }} />,
  };
}

function ChartBlockSkeleton({ height = 240 }) {
  return (
    <div
      style={{
        border: `1px solid ${ui.borderSoft}`,
        borderRadius: ui.radiusSm,
        padding: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Skeleton circle height={18} />
        <Skeleton width={130} height={13} />
      </div>
      <Skeleton height={height - 66} radius={10} />
    </div>
  );
}

function PostCardSkeleton() {
  return (
    <div
      style={{
        flex: '0 0 auto',
        width: 248,
        border: `1px solid ${ui.borderSoft}`,
        borderRadius: 14,
        overflow: 'hidden',
        background: ui.card,
      }}
    >
      <Skeleton height={280} radius={0} />
      <div style={{ padding: '12px 14px 14px' }}>
        <Skeleton width="100%" height={11} />
        <Skeleton width="72%" height={11} style={{ marginTop: 6 }} />
        <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
          <Skeleton width={52} height={13} />
          <Skeleton width={52} height={13} />
        </div>
      </div>
    </div>
  );
}

function HeroSkeleton() {
  return (
    <div
      style={{
        background: ui.card,
        border: `1px solid ${ui.border}`,
        borderRadius: ui.radius,
        boxShadow: ui.shadow,
        overflow: 'hidden',
        fontFamily: ui.font,
      }}
    >
      <Skeleton height={128} radius={0} />
      <div style={{ padding: '0 28px 26px', display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 460px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, marginTop: -48 }}>
            <Skeleton
              circle
              height={128}
              style={{ boxSizing: 'border-box', border: '4px solid #FFFFFF' }}
            />
            <div style={{ flex: 1, maxWidth: 340, paddingBottom: 6 }}>
              <Skeleton width="65%" height={22} />
              <Skeleton width="45%" height={13} style={{ marginTop: 10 }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Skeleton width={72} height={22} radius={999} />
            <Skeleton width={96} height={22} radius={999} />
            <Skeleton width={64} height={22} radius={999} />
          </div>
          <Skeleton width="82%" height={12} style={{ marginTop: 16 }} />
          <Skeleton width="58%" height={12} style={{ marginTop: 7 }} />
        </div>
        <div style={{ flex: '0 1 280px', marginTop: 20 }}>
          <Skeleton height={158} radius={ui.radiusSm} />
        </div>
      </div>
    </div>
  );
}

export default function ProfileSkeleton() {
  return (
    <>
      <HeroSkeleton />
      <SectionCard {...SectionHeaderSkeleton()} actions={<Skeleton width={260} height={28} radius={999} />}>
        <StatTilesSkeleton count={6} />
      </SectionCard>
      <SectionCard {...SectionHeaderSkeleton()}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          <ChartBlockSkeleton />
          <ChartBlockSkeleton />
          <ChartBlockSkeleton />
        </div>
      </SectionCard>
      <SectionCard {...SectionHeaderSkeleton()}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
          <ChartBlockSkeleton height={300} />
          <ChartBlockSkeleton height={300} />
        </div>
      </SectionCard>
      <SectionCard {...SectionHeaderSkeleton()} bodyPadding="16px 24px 12px">
        <div style={{ display: 'flex', gap: 16, overflow: 'hidden', paddingBottom: 6 }}>
          <PostCardSkeleton />
          <PostCardSkeleton />
          <PostCardSkeleton />
          <PostCardSkeleton />
          <PostCardSkeleton />
        </div>
      </SectionCard>
    </>
  );
}
